/** The pickers' value for "nobody". */
export const UNASSIGNED = 'unassigned';

/** Who a task is assigned to. A task carries at most one of these. */
export type TaskAssignee =
  | { type: 'user'; userPublicId: string }
  | { type: 'team'; teamPublicId: string };

/**
 * A task's assignee is a discriminated union; a picker holds it as a single
 * string value (`user:<publicId>`, `team:<publicId>`, or 'unassigned').
 *
 * This is the only definition of that encoding. Every surface holding an
 * assignee as a string goes through it, so one value means the same thing in a
 * picker, a stored template default and a list filter alike.
 */
export const encodeTaskAssignee = (assignee?: TaskAssignee | null): string => {
  if (!assignee) return UNASSIGNED;
  return assignee.type === 'user'
    ? `user:${assignee.userPublicId}`
    : `team:${assignee.teamPublicId}`;
};

export const decodeTaskAssignee = (
  value?: string | null
): TaskAssignee | null => {
  if (!value || value === UNASSIGNED) return null;
  const [kind, ...rest] = value.split(':');
  const ref = rest.join(':');
  if (kind === 'user') return { type: 'user', userPublicId: ref };
  if (kind === 'team') return { type: 'team', teamPublicId: ref };
  return null;
};

/**
 * Encode the `assignedTo` union a task query returns. An unrecognised member
 * reads as unassigned, since the union may gain members.
 */
export const encodeAssignedTo = (
  assignedTo?:
    | { __typename: 'User'; publicId: string }
    | { __typename: 'Team'; id: string }
    | null
): string => {
  if (!assignedTo) return UNASSIGNED;
  if (assignedTo.__typename === 'User')
    return encodeTaskAssignee({
      type: 'user',
      userPublicId: assignedTo.publicId,
    });
  if (assignedTo.__typename === 'Team')
    return encodeTaskAssignee({ type: 'team', teamPublicId: assignedTo.id });
  return UNASSIGNED;
};
